import {
  useState, useEffect, useCallback, createContext, useMemo,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { UpChunk } from '@mux/upchunk';
import uuid from 'uuid';

import { useForceUpdate } from '../../../hooks/index';
import { selectUploadMedia } from '../../../store/modules/articles/selectors';
import {
  uploadArticleMediaSuccess,
  removeUploadArticleMedia,
  deleteArticleRequest,
  uploadNewArticleVideoRequest,
  removeAllUploadArticleMedia,
} from '../../../store/modules/articles/actions';
/* eslint-disable no-param-reassign */

interface UploadContextType {
  streamData: StreamMeta[];
  addStream: (data: AddStreamData) => void;
  remove: (data: StreamMeta) => void;
  removeAll: () => void;
  cancel: (data: StreamMeta) => void;
  cancelAll: () => void;
  restart: (data: StreamMeta) => void;
  openPreviewModal: boolean;
  setOpenPreviewModal: (open: boolean) => void;
  currentStreamId: string;
  setCurrentStreamId: (id: string) => void;
  currentStream: StreamMeta | undefined;
}

const initialState = {
  streamData: [],
  addStream: () => {},
  remove: () => {},
  removeAll: () => {},
  cancel: () => {},
  cancelAll: () => {},
  restart: () => {},
  openPreviewModal: false,
  setOpenPreviewModal: () => {},
  currentStreamId: '',
  setCurrentStreamId: () => {},
  currentStream: undefined,
};

export type StreamMeta = {
  blob: Blob[];
  id: string;
  url: string;
  name: string;
  status: string;
  percent: number;
  upChunk?: UpChunk;
  timestamp: string;
};

type StreamData = {
  blob: Blob;
  id: string;
  name: string;
};

type AddStreamData = {
  blob: Blob;
  name: string;
};

export const UploadRecordContext = createContext<UploadContextType>(initialState);

// 10MB chunk size offers about 2* speed increase, over 1MB chunk size, for larger sized (e.g. 80MB)
const MbToBytes = 1024;
const minChunkSize = 5 * MbToBytes; // Minimum chunk size = 1MB
const maxChunkSize = 20 * MbToBytes; // Max chunk size = 20MB
const targetChunkCount = 10; // 10 chunks, 10% each
const chunkSizeMultipleRequirement = 256; // chunk sizes must be a multiple of this number

export const useUploadRecordFiles = (): UploadContextType => {
  const dispatch = useDispatch();
  const forceUpdate = useForceUpdate();
  const [streamData, setStreamData] = useState<StreamMeta[]>([]);
  const { uploads } = useSelector(selectUploadMedia);
  const { id: projectId } = useParams<{ id: string }>();

  const uploadFileChunk = useCallback((data: StreamMeta) => {
    let dynamicChunkSize = minChunkSize;

    const file = new File(data.blob, data.name);

    if (file.size) {
      dynamicChunkSize = (file.size / targetChunkCount) / MbToBytes;
      dynamicChunkSize = Math.max(minChunkSize, dynamicChunkSize);
      dynamicChunkSize = Math.min(maxChunkSize, dynamicChunkSize);

      // Force to integer
      dynamicChunkSize = Math.floor(dynamicChunkSize);

      // Chunk size must be multiples of 256
      const chunkSizeAdjustment = chunkSizeMultipleRequirement - (dynamicChunkSize % chunkSizeMultipleRequirement);
      dynamicChunkSize += chunkSizeAdjustment;
    }

    const upChunk = new UpChunk({
      endpoint: data.url,
      file,
      chunkSize: dynamicChunkSize,
    });

    // subscribe to events
    upChunk.on('error', () => {
      data.status = 'error_upload';
      forceUpdate();
    });

    upChunk.on('progress', ({ detail: progress }) => {
      data.percent = progress;
      forceUpdate();
    });

    upChunk.on('success', () => {
      data.status = 'done';
      data.timestamp = (new Date()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      dispatch(uploadArticleMediaSuccess(data.id));
      forceUpdate();
    });

    data.status = 'uploading';
    data.upChunk = upChunk;
    forceUpdate();
  }, [dispatch, forceUpdate]);

  const remove = useCallback((data: StreamMeta) => {
    const index = streamData.findIndex((f) => f.id === data.id);
    if (index !== -1) {
      data.status = 'removed';
      streamData.splice(index, 1);
      setStreamData([...streamData]);
      dispatch(removeUploadArticleMedia(data.id));
    }
  }, [streamData, setStreamData, dispatch]);

  const removeAll = useCallback(() => {
    dispatch(removeAllUploadArticleMedia());
    setStreamData([]);
  }, [dispatch, setStreamData]);

  const cancel = useCallback((data: StreamMeta) => {
    if (data.upChunk) {
      data.upChunk.pause();
    }
    const fileByList = uploads.find((item) => item.uploadId === data.id);
    remove(data);
    if (fileByList) {
      dispatch(deleteArticleRequest(fileByList.article.id));
    }
    forceUpdate();
  }, [forceUpdate, uploads, dispatch, remove]);

  const cancelAll = useCallback(() => {
    streamData.forEach((item) => {
      if (item.upChunk) {
        item.upChunk.pause();
      }
      const fileByList = uploads.find((media) => media.uploadId === item.id && item.status !== 'done');
      if (fileByList) {
        dispatch(deleteArticleRequest(fileByList.article.id));
      }
    });
    removeAll();
  }, [dispatch, streamData, uploads, removeAll]);

  const restart = useCallback((data: StreamMeta) => {
    const existsUploadStream = streamData.find((item) => item.id === data.id);
    if (existsUploadStream) {
      existsUploadStream.percent = 0;
      if (existsUploadStream.url) {
        uploadFileChunk(existsUploadStream);
      }
    }
  }, [streamData, uploadFileChunk]);

  const addStream = useCallback(async ({ blob, name }: AddStreamData) => {
    const id = `stream-${uuid()}`;
    const streamMeta: StreamMeta = {
      blob: [blob],
      id,
      status: 'ready',
      percent: 0,
      name,
      url: '',
      timestamp: '',
    };
    streamData.unshift(streamMeta);
    setStreamData([...streamData]);
    const data = {
      title: name,
      projectId: parseInt(projectId, 10),
      uploadId: id,
      recordedInStudio: true,
    };
    dispatch(uploadNewArticleVideoRequest(data));
  }, [dispatch, streamData, setStreamData, projectId]);

  useEffect(() => {
    (async () => {
      uploads.forEach((item) => {
        const existStream = streamData.find((steam) => item.uploadId === steam.id);
        if (existStream) {
          const { status } = existStream;
          if (!['uploading', 'done', 'error_upload', 'exception_upload', 'aborted'].includes(status)) {
            if (item.signedUrl) {
              existStream.url = item.signedUrl.url;
              uploadFileChunk(existStream);
            }
          }
        }
      });
    })();
  }, [uploads, streamData, uploadFileChunk]);

  const [openPreviewModal, setOpenPreviewModal] = useState<boolean>(false);
  const [currentStreamId, setCurrentStreamId] = useState<string>('');

  const currentStream: StreamMeta | undefined = useMemo(() => streamData.find((stream) => stream.id === currentStreamId), [streamData, currentStreamId]);

  return {
    streamData,
    addStream,
    remove,
    removeAll,
    cancelAll,
    cancel,
    restart,
    openPreviewModal,
    setOpenPreviewModal,
    currentStreamId,
    setCurrentStreamId,
    currentStream,
  };
};
